/**
 * FikoCamMini3DBase + paylaşılan tek renderer.
 *
 * SORUN: Her CAM mini-3D (takım ekseni/strateji/makro) ayrı bir tam FikoThreeJsComponent =
 * ayrı WebGL context açıyordu. Tarayıcı limiti (~16) aşılınca EN ESKİ context (ana 3D sahnesi)
 * öldürülüyordu → "3D gitti geldi". forceContextLoss() bu makinede no-op (WEBGL_lose_context yok).
 *
 * ÇÖZÜM (three.js resmi "Multiple Canvases, Single Renderer" tekniği):
 *  - TÜM CAM mini viewer'lar TEK paylaşılan WebGLRenderer kullanır (1 context, uygulama ömrü boyunca).
 *  - Her viewer kendi hafif 2D <canvas>'ına sahne içeriğini drawImage ile blit eder.
 *  - Kaç dialog açılırsa açılsın context sayısı SABİT kalır → tükenme imkânsız.
 *
 * Alt sınıflar `this.sahne.anaGosterim` shim'ini (aktifKamera/canvas/ciz/aktifKontrol) eskisi gibi
 * kullanır — böylece mevcut ciz()/isabet()/cercevele() kodu neredeyse aynen korunur.
 * Kamera: ortografik, Z-yukarı, izometrik. Orbit: SAĞ tuş = döndür, ORTA = kaydır, TEKER = zoom
 * (SOL tuş alt sınıfın seçimi için serbest).
 */
import { Directive, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import * as THREE from 'three';

let _paylasilanR: THREE.WebGLRenderer | null = null;
/** Tüm CAM mini viewer'ların paylaştığı TEK WebGLRenderer (1 GL context). */
export function camMiniRenderer(): THREE.WebGLRenderer {
  if (!_paylasilanR) {
    const cv = document.createElement('canvas');
    _paylasilanR = new THREE.WebGLRenderer({ canvas: cv, antialias: true, alpha: true, premultipliedAlpha: false });
    _paylasilanR.setPixelRatio(1);
    _paylasilanR.setClearColor(0x000000, 0);
    _paylasilanR.outputColorSpace = THREE.SRGBColorSpace;
  }
  return _paylasilanR;
}
const _bdpr = () => Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
/** Sahneyi paylaşılan renderer'da çiz ve hedef 2D context'e blit et. ss=supersample (SSAA → keskin kenar). */
function miniBlit(scene: THREE.Scene, kamera: THREE.Camera, w: number, h: number, ctx: CanvasRenderingContext2D, ss = 1): void {
  const dpr = _bdpr() * Math.max(1, ss);
  const pw = Math.max(1, Math.round(w * dpr)), ph = Math.max(1, Math.round(h * dpr));
  const r = camMiniRenderer();
  if (r.domElement.width !== pw || r.domElement.height !== ph) r.setSize(pw, ph, false);
  r.setViewport(0, 0, pw, ph); r.setScissor(0, 0, pw, ph);
  r.clear(); r.render(scene, kamera);
  const dw = Math.max(1, Math.round(w * _bdpr())), dh = Math.max(1, Math.round(h * _bdpr()));   // ekran backing (ss'siz)
  if (ctx.canvas.width !== dw) ctx.canvas.width = dw;
  if (ctx.canvas.height !== dh) ctx.canvas.height = dh;
  ctx.imageSmoothingEnabled = true; (ctx as any).imageSmoothingQuality = 'high';
  ctx.globalCompositeOperation = 'copy';
  ctx.drawImage(r.domElement, 0, 0, pw, ph, 0, 0, dw, dh);   // ss×→1× küçült = SSAA
}

@Directive()
export abstract class FikoCamMini3DBase implements AfterViewInit, OnDestroy {
  viewerGenislik = 240;
  viewerYukseklik = 200;
  protected abstract viewerContainerRef: ElementRef<HTMLDivElement>;

  protected sahne: any = null;          // THREE.Scene + anaGosterim shim
  protected hazirMi = false;
  protected threeJs: any = null;        // uyumluluk shim (anaGosterim)
  protected toneMappingTip: 'none' | 'aces' = 'none';   // alt sınıf 'aces' → fotorealistik tonlama
  protected toneMappingPoz = 1;
  protected ssaa = 1;                   // supersample faktörü (2 → keskin kenar)

  private kam!: THREE.OrthographicCamera;
  private dispCanvas?: HTMLCanvasElement;
  private dispCtx?: CanvasRenderingContext2D;
  private hedef = new THREE.Vector3();
  private _ro?: ResizeObserver;
  private _rafBekliyor = false;
  private _dragMod: 'rot' | 'pan' | null = null;
  private _last = { x: 0, y: 0 };
  private oDownH?: (e: PointerEvent) => void;
  private oMoveH?: (e: PointerEvent) => void;
  private oUpH?: () => void;
  private oWheelH?: (e: WheelEvent) => void;
  private oCtxH?: (e: Event) => void;

  constructor(protected cd: ChangeDetectorRef, _injector: any, _vcr: any, _envInj: any, protected ngZone: NgZone) {}

  ngAfterViewInit(): void {
    const host = this.viewerContainerRef?.nativeElement; if (!host) return;
    const cv = document.createElement('canvas');
    cv.style.display = 'block'; cv.style.width = '100%'; cv.style.height = '100%'; cv.style.touchAction = 'none';
    host.appendChild(cv);
    this.dispCanvas = cv; this.dispCtx = cv.getContext('2d') || undefined;

    const w = this.viewerGenislik, h = this.viewerYukseklik;
    this.kam = new THREE.OrthographicCamera(-w / 2, w / 2, h / 2, -h / 2, -10000, 10000);
    this.kam.up.set(0, 0, 1); this.kam.position.set(80, -80, 80); this.kam.lookAt(0, 0, 0);

    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const dir = new THREE.DirectionalLight(0xffffff, 0.7); dir.position.set(1, -1, 2); scene.add(dir);
    (scene as any).anaGosterim = {
      aktifKamera: this.kam,
      canvas: { nativeElement: cv },
      sunucu: camMiniRenderer(),
      aktifKontrol: { target: this.hedef, enabled: true, update: () => this.kam.lookAt(this.hedef) },
      ciz: () => this.iste(),
    };
    this.sahne = scene;
    this.threeJs = (scene as any).anaGosterim;

    this.orbitKur();
    this.boyutTakip();
    this.hazirMi = true;
    this.sahneHazir();
    this.iste();
    this.cd.markForCheck();
  }

  /** Alt sınıfta override edilir (sahne kurulumu). */
  protected sahneHazir(): void {}
  /** Alt sınıfta override edilir (kendi listener temizliği). */
  protected temizle(): void {}

  /** Bir kare blit iste (rAF ile çoklu çağrı tek kareye iner). */
  protected iste(): void {
    if (this._rafBekliyor) return; this._rafBekliyor = true;
    requestAnimationFrame(() => { this._rafBekliyor = false; this.blit(); });
  }
  private blit(): void {
    if (!this.dispCtx || !this.sahne) return;
    this.kam.updateMatrixWorld();
    const r = camMiniRenderer();
    r.toneMapping = this.toneMappingTip === 'aces' ? THREE.ACESFilmicToneMapping : THREE.NoToneMapping;
    r.toneMappingExposure = this.toneMappingPoz;
    miniBlit(this.sahne, this.kam, this.viewerGenislik, this.viewerYukseklik, this.dispCtx, this.ssaa);
  }

  /** Uyumluluk: alt sınıflar fixRendererSize(this.threeJs) çağırır → kamera frustum'unu güncelle. */
  protected fixRendererSize(_?: any): void {
    if (!this.kam) return;
    const w = this.viewerGenislik, h = this.viewerYukseklik;
    this.kam.left = -w / 2; this.kam.right = w / 2; this.kam.top = h / 2; this.kam.bottom = -h / 2;
    this.kam.updateProjectionMatrix(); this.iste();
  }

  private boyutTakip(): void {
    const host = this.viewerContainerRef?.nativeElement; if (!host || typeof ResizeObserver === 'undefined') return;
    this._ro = new ResizeObserver(() => {
      const w = Math.round(host.clientWidth), h = Math.round(host.clientHeight);
      if (w < 10 || h < 10) return;
      if (Math.abs(w - this.viewerGenislik) < 3 && Math.abs(h - this.viewerYukseklik) < 3) return;
      this.ngZone.runOutsideAngular(() => { this.viewerGenislik = w; this.viewerYukseklik = h; this.fixRendererSize(); (this as any).cercevele?.(); });
    });
    this._ro.observe(host);
  }

  // ---- Orbit: SAĞ=döndür · ORTA=kaydır · TEKER=zoom (SOL → alt sınıf seçimi) ----
  private orbitKur(): void {
    const cv = this.dispCanvas; if (!cv) return;
    this.oDownH = (e) => {
      if (e.button === 2) this._dragMod = 'rot'; else if (e.button === 1) this._dragMod = 'pan'; else return;
      e.preventDefault(); this._last = { x: e.clientX, y: e.clientY };
      window.addEventListener('pointermove', this.oMoveH!, true); window.addEventListener('pointerup', this.oUpH!, true);
    };
    this.oMoveH = (e) => {
      const dx = e.clientX - this._last.x, dy = e.clientY - this._last.y; this._last = { x: e.clientX, y: e.clientY };
      if (this._dragMod === 'rot') this.donder(dx, dy); else if (this._dragMod === 'pan') this.kaydir(dx, dy);
    };
    this.oUpH = () => { this._dragMod = null; window.removeEventListener('pointermove', this.oMoveH!, true); window.removeEventListener('pointerup', this.oUpH!, true); };
    this.oWheelH = (e) => { e.preventDefault(); const f = e.deltaY < 0 ? 1.12 : 0.89; this.kam.zoom = Math.max(0.05, this.kam.zoom * f); this.kam.updateProjectionMatrix(); this.iste(); };
    this.oCtxH = (e) => e.preventDefault();
    cv.addEventListener('pointerdown', this.oDownH);
    cv.addEventListener('wheel', this.oWheelH, { passive: false });
    cv.addEventListener('contextmenu', this.oCtxH);
  }
  private donder(dx: number, dy: number): void {
    const off = this.kam.position.clone().sub(this.hedef); const r = off.length() || 1;
    let az = Math.atan2(off.y, off.x); let pol = Math.acos(THREE.MathUtils.clamp(off.z / r, -1, 1));
    az -= dx * 0.01; pol = THREE.MathUtils.clamp(pol - dy * 0.01, 0.05, Math.PI - 0.05);
    const sp = Math.sin(pol);
    off.set(r * sp * Math.cos(az), r * sp * Math.sin(az), r * Math.cos(pol));
    this.kam.position.copy(this.hedef).add(off); this.kam.up.set(0, 0, 1); this.kam.lookAt(this.hedef); this.iste();
  }
  private kaydir(dx: number, dy: number): void {
    const sc = 1 / (this.kam.zoom || 1);
    const right = new THREE.Vector3().setFromMatrixColumn(this.kam.matrix, 0);
    const up = new THREE.Vector3().setFromMatrixColumn(this.kam.matrix, 1);
    const mv = right.multiplyScalar(-dx * sc).add(up.multiplyScalar(dy * sc));
    this.hedef.add(mv); this.kam.position.add(mv); this.iste();
  }

  ngOnDestroy(): void {
    if (this._ro) { this._ro.disconnect(); this._ro = undefined; }
    const cv = this.dispCanvas;
    if (cv) {
      if (this.oDownH) cv.removeEventListener('pointerdown', this.oDownH);
      if (this.oWheelH) cv.removeEventListener('wheel', this.oWheelH);
      if (this.oCtxH) cv.removeEventListener('contextmenu', this.oCtxH);
    }
    if (this.oMoveH) window.removeEventListener('pointermove', this.oMoveH, true);
    if (this.oUpH) window.removeEventListener('pointerup', this.oUpH, true);
    try { this.temizle(); } catch (e) { }
    // Sahne nesnelerini boşalt — paylaşılan renderer'a DOKUNMA (1 context kalıcı).
    try { this.sahne?.traverse?.((o: any) => { o.geometry?.dispose?.(); const m = o.material; if (m) (Array.isArray(m) ? m : [m]).forEach((x: any) => { x.map?.dispose?.(); x.dispose?.(); }); }); } catch (e) { }
    if (cv && cv.parentElement) cv.parentElement.removeChild(cv);
    this.dispCanvas = undefined; this.dispCtx = undefined; this.sahne = null; this.threeJs = null;
  }
}
